import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { orderingService } from '../../services/orderingService';
import { loadGuestCart, saveGuestCart, clearGuestCart } from '../../utils/cartStorage';
import { useGuestStay } from '../../hooks/useGuestStay';
import { formatCurrency } from '../../utils/currency';
import { ApiError, BookingDetails, CartItem, MenuCategory, MenuItem, Order } from '../../types';
import { GDTop, GDDone, GDSpinnerScreen } from './GDBits';

interface FoodScreenProps {
  booking: BookingDetails;
}

const FOOD_EMOJI = ['🍛', '🥘', '🍲', '🥗', '🍜', '🍳', '🥪', '🍰', '☕', '🥤'];

/**
 * Room service ordering: category chips → item list with steppers → deliver-to
 * → one sticky order button. Orders charge to the room folio when possible.
 */
const FoodScreen: React.FC<FoodScreenProps> = ({ booking }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canOrderFood, isCheckedIn, currency, hotelInfo } = useGuestStay(booking);

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>(() => loadGuestCart(booking).items || []);
  const [deliverTo, setDeliverTo] = useState<'room' | 'restaurant' | 'lobby'>('room');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placedOrder, setPlacedOrder] = useState<Order | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['gdMenuCategories'],
    queryFn: async () => {
      const response = await orderingService.getMenuCategories();
      const data: any = response.data;
      const list: MenuCategory[] = Array.isArray(data) ? data : data?.data || data?.results || [];
      return list;
    },
    staleTime: 5 * 60 * 1000,
  });
  const categories = useMemo(() => categoriesQuery.data || [], [categoriesQuery.data]);

  useEffect(() => {
    if (!categoryId && categories.length) setCategoryId(String(categories[0].id));
  }, [categories, categoryId]);

  const itemsQuery = useQuery({
    queryKey: ['gdMenuItems', categoryId],
    queryFn: async () => {
      const response = await orderingService.getMenuItemsByCategory(categoryId as string);
      const data: any = response.data;
      const list: MenuItem[] = Array.isArray(data) ? data : data?.data || data?.results || [];
      return list;
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });
  const items = itemsQuery.data || [];

  const qtyOf = (item: MenuItem) =>
    cart.find(entry => String(entry.menu_item.id) === String(item.id))?.quantity || 0;

  const changeQty = (item: MenuItem, delta: number) => {
    setCart(prev => {
      const next = [...prev];
      const index = next.findIndex(entry => String(entry.menu_item.id) === String(item.id));
      if (index === -1) {
        if (delta > 0) {
          next.push({ menu_item: item, quantity: delta, total_price: item.price * delta });
        }
      } else {
        const quantity = Math.max(0, next[index].quantity + delta);
        if (quantity === 0) next.splice(index, 1);
        else next[index] = { ...next[index], quantity, total_price: item.price * quantity };
      }
      saveGuestCart(next, booking, deliverTo);
      return next;
    });
  };

  const cartCount = cart.reduce((sum, entry) => sum + entry.quantity, 0);
  const cartTotal = cart.reduce((sum, entry) => sum + entry.total_price, 0);

  const placeOrder = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await orderingService.placeOrder({
        items: cart.map(entry => ({
          menu_item_id: entry.menu_item.id,
          quantity: entry.quantity,
          ...(entry.special_instructions
            ? { special_instructions: entry.special_instructions }
            : {}),
        })) as any,
        delivery_location: deliverTo,
      });
      const data: any = response.data;
      setPlacedOrder((data && data.data) || data);
      clearGuestCart(booking);
      setCart([]);
    } catch (err) {
      setError((err as ApiError).error || t('gd.checkin.genericError'));
    } finally {
      setBusy(false);
    }
  };

  if (placedOrder) {
    const chargedToRoom = placedOrder.billing_status !== 'cash_payment_required';
    return (
      <section className="gd-screen">
        <GDDone title={t('gd.food.placedTitle')}>
          <p>
            {t('gd.food.placedBody', { number: placedOrder.order_number || placedOrder.id })}
          </p>
          <span className={`gd-pill ${chargedToRoom ? '' : 'gd-orange'}`}>
            {chargedToRoom ? t('gd.food.chargedToRoom') : t('gd.food.payOnDelivery')}
          </span>
          <button type="button" className="gd-btn" onClick={() => setPlacedOrder(null)}>
            {t('gd.food.orderMore')}
          </button>
          <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
            {t('gd.common.backHome')}
          </button>
        </GDDone>
      </section>
    );
  }

  return (
    <section className="gd-screen">
      <GDTop title={hotelInfo?.hotel?.button_labels?.['food'] || t('gd.food.title')} backTo="/home" />
      <div className="gd-body">
        {!canOrderFood && (
          <div className="gd-note">
            {isCheckedIn ? t('gd.food.notEnabled') : t('gd.food.afterCheckin')}
          </div>
        )}
        {categoriesQuery.isLoading && <GDSpinnerScreen />}
        {!!categories.length && (
          <div className="gd-chips">
            {categories.map(category => (
              <button
                key={category.id}
                type="button"
                className={`gd-chip${String(category.id) === categoryId ? ' gd-on' : ''}`}
                onClick={() => setCategoryId(String(category.id))}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
        <div className="gd-card gd-list">
          {itemsQuery.isLoading && <p className="gd-muted">{t('gd.common.loading')}</p>}
          {!itemsQuery.isLoading && !items.length && (
            <p className="gd-muted">{t('gd.food.emptyCategory')}</p>
          )}
          {items.map((item, index) => (
            <div className="gd-item" key={item.id}>
              <div className="gd-thumb">
                {item.image_url
                  ? <img src={item.image_url} alt="" />
                  : FOOD_EMOJI[index % FOOD_EMOJI.length]}
              </div>
              <div>
                <b>{item.name}</b>
                <small>
                  {[item.description, formatCurrency(item.price, currency)]
                    .filter(Boolean).join(' · ')}
                </small>
              </div>
              <div className="gd-qty">
                <button type="button" onClick={() => changeQty(item, -1)} aria-label="less">−</button>
                <span>{qtyOf(item)}</span>
                <button
                  type="button"
                  onClick={() => item.is_available !== false && changeQty(item, 1)}
                  aria-label="more"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="gd-field">
          <label>{t('gd.food.deliverTo')}</label>
          <div className="gd-chips">
            {(['room', 'restaurant', 'lobby'] as const).map(location => (
              <button
                key={location}
                type="button"
                className={`gd-chip${deliverTo === location ? ' gd-on' : ''}`}
                onClick={() => setDeliverTo(location)}
              >
                {location === 'room' && booking.room_number
                  ? t('gd.food.room', { room: booking.room_number })
                  : t(`gd.food.location.${location}`)}
              </button>
            ))}
          </div>
        </div>
        {error && (
          <div className="gd-note" style={{ background: 'var(--gd-red-soft)', color: 'var(--gd-red)' }}>
            {error}
          </div>
        )}
        {cartCount > 0 && (
          <div className="gd-total">
            <button
              type="button"
              className="gd-btn gd-orange gd-row"
              disabled={busy || !canOrderFood}
              onClick={placeOrder}
            >
              <span>
                {t('gd.food.cartSummary', {
                  count: cartCount, total: formatCurrency(cartTotal, currency),
                })}
              </span>
              <span>{busy ? t('gd.common.working') : t('gd.food.placeOrder')}</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FoodScreen;
